import LoadingButton from '@mui/lab/LoadingButton';
import { Box, CircularProgress, Container, Fade, Modal, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ModalHeader } from '~/components';
import { MessageList } from '~/components/Chat';
import { Message } from '~/components/Chat/types';
import TagManager from '~/services/gtm';
import {
  getUserDetails,
  saveMobilePhone,
  sendCode,
  UserDetailsResponse,
  validateCode,
  ValidateCodeResponse
} from '~/services/modules/users';
import useStore, { storeState } from '~/store';
import { DocumentType } from '~/constants/documentType';
import { parseParamToString } from '~/utils/parseParamToString';
import { resolveFlowPlanId } from '~/utils/flowPlanId';
import { reportError } from '~/utils/reportError';
import { searchNameEquipmentId } from '~/utils/searchNameEquipment';
import { Club, Distributor, Seller } from './hooks/use-get-plans';
import MESSAGES from './messages';
import { container, containerMessageList } from './styles';
import { codeVerificationMessages } from './utils/handleMessages';
import { STEP_PHONE, handlePreviousStep, handleSteps } from './utils/handleSteps';
import { optionalBirthDate } from '~/pages/PersonalCoverage/newJourney/quotePayload';

const HTTP_CONFLICT = 409;

const initialMessages: Message[] = [];

const QUOTE_CREATION_TIMEOUT_MS = 15_000;

interface BasicModalProps {
  steps: any;
  club?: Club;
  distributor?: Distributor;
  sellerInfo?: Seller | null;
  isOpen?: boolean;
  onClose?: () => void;
}

export default function BasicModal({ steps, club, distributor, sellerInfo, isOpen, onClose }: BasicModalProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Exibir "Nome do vendedor" apenas quando vier nome da API (seller ou distributor)
  const vendorDisplayName =
    (typeof sellerInfo === 'object' && sellerInfo?.name ? sellerInfo.name : null) ?? distributor?.name ?? null;
  const showVendorCaption = Boolean(vendorDisplayName);

  const { quote, quoteAsyncPost, setContinents, setStepChat, SetCustomerId, setGender } = useStore((store) => ({
    quote: store.quote,
    quoteAsyncPost: store.quoteAsyncPost,
    setContinents: store.setContinents,
    setStepChat: store.setStepChat,
    SetCustomerId: store.SetCustomerId,
    setGender: store.setGender
  }));

  const {
    equipment,
    stepChat,
    hasInvoice,
    name,
    email,
    phoneNumber,
    cpf,
    gender,
    customer_id,
    distributor_id,
    organization_id,
    seller,
    plan_id,
    accessoriesValue,
    continents,
    internationalCoverage,
    isNewEquipment,
    participatesInCompetitions,
    equipmentMaterial,
    equipmentCondition,
    yearOfManufacture,
    placeStore,
    isSocialName,
    birthDate
  } = quote;

  const equipmentValue = quote.equipmentValue;
  const [open, setOpen] = useState(isOpen ?? true);
  const [hasValidValue, setHasValidValue] = useState({
    name: false,
    email: false,
    cpf: false
  });
  const [loading, setLoading] = useState(false);
  const [quotationSubmitting, setQuotationSubmitting] = useState(false);
  const [disableStepHandler, setDisableStepHandler] = useState(false);
  const [messages, setMessages] = useState(initialMessages);
  const [userData, setUserData] = useState<UserDetailsResponse | null>(null);
  const [usedCodeType, setUsedCodeType] = useState<number>();
  // CORE-1025: token do validate-code (autoriza gravar o celular) e o celular digitado no passo do telefone
  const [sessionToken, setSessionToken] = useState('');
  const [validatedGender, setValidatedGender] = useState<string | undefined>();
  const [phoneInput, setPhoneInput] = useState({ value: '', isValid: false });

  useEffect(() => {
    if (isOpen !== undefined) {
      setOpen(isOpen);
    }
  }, [isOpen]);

  const handleClose = () => {
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };
  const removeMessage = (key: number) => {
    const newMessages = [...messages];
    newMessages.splice(
      newMessages.findIndex(({ id }) => id === key),
      1
    );
    setMessages(newMessages);
  };

  const handleValidateInputs = (key: 'email' | 'name' | 'cpf', isValid: boolean) => {
    setHasValidValue((prevState) => ({ ...prevState, [key]: isValid }));
  };

  const handleQuotation = async () => {
    // No fluxo autorizado (código validado no login), customer_id vem do person_id da resposta validate-code
    const quoteBody = {
      customer: {
        ...(customer_id && { customer_id }),
        name,
        email,
        mobile_phone: phoneNumber.replace(/\D/g, '').slice(0, 11),
        document: cpf,
        document_type: DocumentType.CPF,
        gender,
        is_social_name: isSocialName,
        ...optionalBirthDate(birthDate)
      },
      equipment_type_id: searchNameEquipmentId(equipment),
      plan_id: resolveFlowPlanId(searchParams.get('plan'), plan_id),
      distributor_id: distributor_id ?? searchParams.get('dis'),
      ...(organization_id != null && organization_id !== '' && { organization_id }),
      ...(seller != null && seller !== '' && { seller }),
      quotation_value: equipmentValue,
      value_of_accessories: accessoriesValue,
      has_invoice: hasInvoice,
      is_new: isNewEquipment,
      participates_competition: participatesInCompetitions,
      equipment_material_type: equipmentMaterial,
      equipment_conditions: equipmentCondition,
      international_coverage: continents,
      place_store: placeStore,
      year_of_manufacture: yearOfManufacture
    };

    setLoading(true);
    setQuotationSubmitting(true);
    const timeoutError = new Error('QUOTE_CREATION_TIMEOUT');
    try {
      await Promise.race([
        quoteAsyncPost(quoteBody),
        new Promise<never>((_, reject) => {
          window.setTimeout(() => reject(timeoutError), QUOTE_CREATION_TIMEOUT_MS);
        })
      ]);
      const planParam = resolveFlowPlanId(searchParams.get('plan'), plan_id);
      const distribuitorParam = searchParams.get('dis');
      const orgParam = searchParams.get('org') ?? organization_id ?? '';
      const sellerParam = searchParams.get('seller') ?? seller ?? '';

      const quoteStore = useStore.getState();
      const quoteHash = quoteStore.quoteResponse?.quote_hash;

      const queryTail = `${distribuitorParam ? `&dis=${distribuitorParam}` : ''}${orgParam ? `&org=${orgParam}` : ''}${
        sellerParam ? `&seller=${sellerParam}` : ''
      }`;

      if (quoteHash) {
        navigate(`/personalCoverage?quote=${quoteHash}&plan=${planParam}${queryTail}`);
      } else {
        navigate(`/personalCoverage?plan=${planParam}${queryTail}`);
      }
    } catch (error: any) {
      if (error instanceof Error && error.message === 'QUOTE_CREATION_TIMEOUT') {
        storeState.addToast({
          title: 'Demora na cotação',
          message: 'A operação ultrapassou 15 segundos. Verifique a sua ligação e tente novamente.',
          type: 'warning'
        });
      } else {
        reportError('Erro ao processar cotação simplificada', error, {
          context: 'CalcSimplifiedCoverage_handleButtonNext',
          step: steps,
          planParam: parseParamToString(searchParams.get('plan') ?? undefined),
          distribuitorParam: parseParamToString(searchParams.get('dis') ?? undefined)
        });
        let errorMessage =
          'Não foi possível processar a sua cotação no momento. Por favor, tente novamente mais tarde.';
        if (error.isAxiosError) {
          const notifications = error.response?.data?.notifications;
          if (notifications) errorMessage = notifications[0]?.message;
        }
        storeState.addToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
      }
    } finally {
      setLoading(false);
      setQuotationSubmitting(false);
    }
  };

  const checkUserDetails = async (document: string): Promise<UserDetailsResponse | null> => {
    try {
      const userData = await getUserDetails(document);
      return userData;
    } catch (error: any) {
      // Se o erro for 404 ou qualquer outro, retorna null
      console.log('Usuário não encontrado ou erro na consulta:', error);
      return null;
    }
  };

  const sendCodeToUser = async (document: string, codeType: number): Promise<any> => {
    try {
      setUsedCodeType(codeType); // Armazena o codeType usado para usar na validação
      console.log('Enviando código com codeType:', codeType);
      const result = await sendCode(document, codeType);
      console.log('Código enviado com sucesso:', result);
      return result;
    } catch (error: any) {
      reportError('Erro ao enviar código de verificação', error, {
        codeType: codeType,
        document: document,
        status: error?.response?.status,
        data: error?.response?.data
      });

      const errorMessage =
        error?.response?.data?.notifications?.[0]?.message ||
        'Não foi possível enviar o código de verificação. Tente novamente.';

      storeState.addToast({
        title: 'Erro',
        message: errorMessage,
        type: 'error'
      });
      throw error;
    }
  };

  const validateCodeFromUser = async (code: string): Promise<ValidateCodeResponse> => {
    if (!userData) {
      throw new Error('Dados do usuário não encontrados');
    }

    if (!usedCodeType) {
      throw new Error('Método de envio não foi selecionado');
    }

    try {
      console.log('Validando código com codeType:', usedCodeType);
      const result = await validateCode(userData.document_number, code, usedCodeType, organization_id ?? undefined);
      console.log('Código validado com sucesso:', result);

      setSessionToken(result.token ?? '');
      setValidatedGender(result.gender);

      // Salva o person_id da resposta (200) como customer_id na store para envio na cotação
      if (result.person_id) {
        SetCustomerId(result.person_id);
      }

      // O cadastro já pode trazer o género; quando vem, segue direto no payload.
      if (result.gender) {
        setGender(result.gender as 'm' | 'f');
      }

      return result;
    } catch (error: any) {
      reportError('Erro ao validar código de verificação', error, {
        codeType: usedCodeType,
        document: userData.document_number,
        status: error?.response?.status,
        data: error?.response?.data
      });

      if (error?.response?.status === HTTP_CONFLICT) {
        SetCustomerId('');
      }

      const errorMessage = error?.response?.data?.notifications?.[0]?.message || 'Código inválido. Tente novamente.';

      storeState.addToast({
        title: 'Erro',
        message: errorMessage,
        type: 'error'
      });
      throw error;
    }
  };

  const savePhoneToUser = async (phone: string) => {
    try {
      return await saveMobilePhone(phone, sessionToken);
    } catch (error: any) {
      reportError('Erro ao salvar celular após validar o código', error, {
        document: userData?.document_number,
        status: error?.response?.status,
        data: error?.response?.data
      });
      throw error;
    }
  };

  const handlePhoneChange = (value: string, isValid: boolean) => setPhoneInput({ value, isValid });

  const {
    handleName,
    handleNextSteps,
    handleAfterCpf,
    buildUserConfirmationFlow,
    handleAfterCodeValidated,
    handlePhoneSubmit
  } = handleSteps({
    setDisableStepHandler,
    setMessages,
    MESSAGES,
    setStepChat,
    removeMessage,
    internationalCoverage,
    equipment,
    name,
    email,
    cpf,
    checkUserDetails,
    sendCodeToUser,
    setUserData,
    setCustomerId: SetCustomerId,
    savePhoneToUser,
    onPhoneChange: handlePhoneChange
  });

  const handleGoBack = () => {
    const userConfirmationFlow = userData ? buildUserConfirmationFlow(userData) : {};

    handlePreviousStep(
      { currentStep: stepChat, setStepChat, steps },
      { MESSAGES, setDisableStepHandler, setMessages },
      removeMessage,
      equipment,
      name,
      // customer_id só existe depois de validar o código, então marca quem entrou pelo cadastro encontrado
      { ...userConfirmationFlow, cameFromUserConfirmation: Boolean(customer_id) }
    );
  };

  useEffect(() => {
    if (stepChat === 10) {
      if (internationalCoverage) {
        if (messages.length === 0) {
          const message = {
            ...MESSAGES[26],
            content: MESSAGES[26].content?.replace('{name}', name)
          };
          setMessages(() => [message, MESSAGES[27], MESSAGES[28], MESSAGES[29], MESSAGES[32], MESSAGES[33]]);
        } else {
          setMessages((messages) => [...messages, MESSAGES[28], MESSAGES[29], MESSAGES[32], MESSAGES[33]]);
        }
      } else {
        const message = {
          ...MESSAGES[26],
          content: MESSAGES[26].content?.replace('{name}', name)
        };
        setMessages(() => [message, MESSAGES[27]]);
        setContinents(1);
      }
    }
  }, [internationalCoverage]);

  useEffect(() => {
    if (stepChat === 1) {
      setStepChat(1);
      handleName();
    }
  }, [stepChat]);

  useEffect(() => {
    if (stepChat === 12) {
      const handleCodeSubmit = async (code: string) => {
        try {
          console.log('Validando código:', code);

          // Valida o código via API
          const result = await validateCodeFromUser(code);

          // Cadastro sem telefone pede o celular (CORE-1025); sem género pede o género
          // (senão o payload da cotação iria com `gender` vazio); senão segue para o equipamento.
          setPhoneInput({ value: '', isValid: false });
          handleAfterCodeValidated(result, userData);
        } catch (error) {
          console.error('Erro ao validar código:', error);
          // O erro já é tratado na função validateCodeFromUser
          // Não avança para próximo step se houver erro
        }
      };

      // Passa a função de reenvio de código para o componente
      const handleResendCode = async () => {
        if (userData && usedCodeType) {
          try {
            // Usa o mesmo codeType que foi usado no envio original
            await sendCodeToUser(userData.document_number, usedCodeType);
            storeState.addToast({
              title: 'Sucesso',
              message: 'Código reenviado com sucesso!',
              type: 'success'
            });
          } catch (error) {
            // Erro já tratado na função sendCodeToUser
          }
        }
      };

      codeVerificationMessages(
        { MESSAGES, setDisableStepHandler, setMessages },
        name,
        handleCodeSubmit,
        handleResendCode,
        usedCodeType as 1 | 2 | undefined
      );
    }
  }, [stepChat]);

  const tagManager = new TagManager();

  const trackName = (name: string) => {
    tagManager.dataLayer({
      event: 'Tracking',
      category: 'track-name',
      action: 'get-user-name',
      value: name
    });
  };

  const trackEmail = (email: string) => {
    tagManager.dataLayer({
      event: 'Tracking',
      category: 'track-email',
      action: 'get-user-email',
      value: email
    });
  };

  const trackCpf = (cpf: string) => {
    tagManager.dataLayer({
      event: 'Tracking',
      category: 'track-cpf',
      action: 'get-user-cpf',
      value: cpf
    });
  };

  return (
    <Container maxWidth="sm">
      {open && (
        <>
          <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Fade in={open}>
              <Box sx={container}>
                <ModalHeader
                  title={club ? `Assine seu ${club.club_name}` : 'Cálculo simplificado de cobertura'}
                  subtitle={showVendorCaption ? `Nome do vendedor: ${vendorDisplayName}` : undefined}
                  visibleGoBack={stepChat !== 1}
                  onClickClose={handleClose}
                  onClickBack={handleGoBack}
                  disableGoBack={disableStepHandler}
                />
                <Box
                  sx={{
                    padding: '1rem 0',
                    display: 'flex',
                    flexDirection: 'row',
                    maxHeight: '420px',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    '&::-webkit-scrollbar': {
                      width: '.5rem'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#FFF',
                      borderRadius: '4px'
                    }
                  }}
                >
                  <Box sx={containerMessageList}>
                    <MessageList handleValidateInputs={handleValidateInputs} messages={messages} steps={steps} />
                  </Box>
                </Box>
                {stepChat === 1 && (
                  <Box
                    sx={{
                      minWidth: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <LoadingButton
                      disabled={name.length <= 3 || disableStepHandler || !hasValidValue.name}
                      size="large"
                      variant={name.length <= 3 || disableStepHandler || !hasValidValue.name ? 'outlined' : 'contained'}
                      color="primary"
                      onClick={() => {
                        handleNextSteps({ currentStep: stepChat, steps });
                        trackName(name);
                      }}
                      loading={loading}
                    >
                      Próximo passo
                    </LoadingButton>
                  </Box>
                )}
                {stepChat === 2 && (
                  <Box
                    sx={{
                      minWidth: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <LoadingButton
                      disabled={disableStepHandler || !hasValidValue.email}
                      size="large"
                      variant={disableStepHandler || !hasValidValue.email ? 'outlined' : 'contained'}
                      color="primary"
                      onClick={() => {
                        handleNextSteps({ currentStep: stepChat, steps });
                        trackEmail(email);
                      }}
                      loading={loading}
                    >
                      Próximo passo
                    </LoadingButton>
                  </Box>
                )}
                {stepChat === 3 && (
                  <Box
                    sx={{
                      minWidth: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <LoadingButton
                      disabled={disableStepHandler || !hasValidValue.cpf}
                      size="large"
                      variant={disableStepHandler || !hasValidValue.cpf ? 'outlined' : 'contained'}
                      color="primary"
                      onClick={() => {
                        handleAfterCpf(stepChat, steps);
                        trackCpf(cpf);
                      }}
                      loading={loading}
                    >
                      Próximo passo
                    </LoadingButton>
                  </Box>
                )}
                {stepChat === 4 && (
                  <Box
                    sx={{
                      minWidth: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <LoadingButton
                      disabled={!gender || disableStepHandler}
                      size="large"
                      variant={!gender || disableStepHandler ? 'outlined' : 'contained'}
                      color="primary"
                      onClick={() => handleNextSteps({ currentStep: stepChat, steps })}
                      loading={loading}
                    >
                      Próximo passo
                    </LoadingButton>
                  </Box>
                )}
                {stepChat === STEP_PHONE && (
                  <Box
                    sx={{
                      minWidth: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <LoadingButton
                      disabled={!phoneInput.isValid || disableStepHandler}
                      size="large"
                      variant={!phoneInput.isValid || disableStepHandler ? 'outlined' : 'contained'}
                      color="primary"
                      onClick={() => handlePhoneSubmit(phoneInput.value, validatedGender, userData)}
                      loading={loading}
                    >
                      Próximo passo
                    </LoadingButton>
                  </Box>
                )}
                {stepChat === 5 && (
                  <Box
                    sx={{
                      minWidth: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <LoadingButton
                      disabled={equipment === 'false'}
                      size="large"
                      variant={equipment === 'false' ? 'outlined' : 'contained'}
                      color="primary"
                      onClick={() => handleNextSteps({ currentStep: stepChat, steps })}
                      loading={loading}
                    >
                      Próximo passo
                    </LoadingButton>
                  </Box>
                )}
                {stepChat === 6 && (
                  <Box
                    sx={{
                      minWidth: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <LoadingButton
                      disabled={
                        equipmentValue < 1000 ||
                        equipmentValue > 100000 ||
                        !yearOfManufacture ||
                        disableStepHandler ||
                        isNewEquipment === null ||
                        hasInvoice === null
                      }
                      size="large"
                      variant={
                        equipmentValue < 1000 || equipmentValue > 100000 || !yearOfManufacture || disableStepHandler
                          ? 'outlined'
                          : 'contained'
                      }
                      color="primary"
                      onClick={() => handleNextSteps({ currentStep: stepChat, steps })}
                      loading={loading}
                    >
                      Próximo passo
                    </LoadingButton>
                  </Box>
                )}
                {stepChat === 7 && (
                  <Box
                    sx={{
                      minWidth: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <LoadingButton
                      disabled={Number(accessoriesValue) > Number(equipmentValue) * 0.1 || disableStepHandler}
                      size="large"
                      variant={
                        Number(accessoriesValue) > Number(equipmentValue) * 0.1 || disableStepHandler
                          ? 'outlined'
                          : 'contained'
                      }
                      color="primary"
                      onClick={() => handleNextSteps({ currentStep: stepChat, steps })}
                      loading={loading}
                    >
                      Próximo passo
                    </LoadingButton>
                  </Box>
                )}
                {stepChat === 8 && (
                  <Box
                    sx={{
                      minWidth: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <LoadingButton
                      disabled={disableStepHandler}
                      size="large"
                      variant={disableStepHandler ? 'outlined' : 'contained'}
                      color="primary"
                      onClick={() => handleNextSteps({ currentStep: stepChat, steps })}
                      loading={loading}
                    >
                      Próximo passo
                    </LoadingButton>
                  </Box>
                )}
                {stepChat === 9 && (
                  <Box
                    sx={{
                      minWidth: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <LoadingButton
                      disabled={disableStepHandler}
                      size="large"
                      variant={disableStepHandler ? 'outlined' : 'contained'}
                      color="primary"
                      onClick={() => handleNextSteps({ currentStep: stepChat, steps })}
                      loading={loading}
                    >
                      Próximo passo
                    </LoadingButton>
                  </Box>
                )}
                {(stepChat === 0 || stepChat === 10) && (
                  <Box
                    sx={{
                      minWidth: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <LoadingButton
                      disabled={(internationalCoverage && (continents === 1 || placeStore === 1)) || disableStepHandler}
                      size="large"
                      variant={
                        (internationalCoverage && continents === 1 && placeStore === 1) || disableStepHandler
                          ? 'outlined'
                          : 'contained'
                      }
                      color="primary"
                      onClick={() => handleQuotation()}
                      loading={loading || quotationSubmitting}
                    >
                      Próximo passo
                    </LoadingButton>
                  </Box>
                )}{' '}
              </Box>
            </Fade>
          </Modal>
          {quotationSubmitting ? (
            <Box
              aria-live="polite"
              role="status"
              sx={{
                alignItems: 'center',
                bgcolor: 'rgba(0, 0, 0, 0.55)',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                inset: 0,
                justifyContent: 'center',
                position: 'fixed',
                zIndex: 1400
              }}
            >
              <CircularProgress aria-hidden color="primary" size={48} />
              <Typography align="center" sx={{ color: 'common.white', maxWidth: 320, px: 2 }} variant="subtitle1">
                A criar a sua cotação…
              </Typography>
            </Box>
          ) : null}
        </>
      )}
    </Container>
  );
}
